import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Bookmark, User, LogOut, ChevronDown, Gift, CalendarCheck } from "lucide-react";

export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const firstName = user.name?.trim().split(/\s+/)[0] || "You";
  const initial = (firstName[0] || "Y").toUpperCase();
  const avatar = user.custom_picture_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${user.custom_picture_path}`
    : user.picture || user.avatar;

  const itemCls =
    "flex items-center gap-3 w-full px-4 py-2.5 text-left text-sm text-[#2A2823] hover:bg-[#B79256]/12 transition-colors";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid="profile-chip"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${firstName} — open profile menu`}
        onClick={() => setOpen((v) => !v)}
        className={`group flex items-center gap-2 pl-1 pr-2 h-10 rounded-full border transition-colors ${
          open
            ? "border-[#B79256] bg-[#B79256]/12"
            : "border-[#B79256]/40 bg-[#FBF8F1] hover:bg-[#B79256]/12 hover:border-[#B79256]/70"
        }`}
      >
        {avatar ? (
          <img
            src={avatar}
            alt=""
            referrerPolicy="no-referrer"
            className="w-8 h-8 shrink-0 rounded-full aspect-square object-cover ring-2 ring-[#B79256]/40 group-hover:ring-[#B79256] transition"
            draggable={false}
          />
        ) : (
          <span className="w-8 h-8 shrink-0 aspect-square rounded-full ring-2 ring-[#B79256]/40 group-hover:ring-[#B79256] bg-[#4E5A3D] text-[#FBF8F1] text-sm font-semibold flex items-center justify-center transition">
            {initial}
          </span>
        )}
        <span
          data-testid="profile-chip-name"
          className="hidden sm:inline text-sm text-[#2A2823] max-w-[7rem] truncate"
        >
          {firstName}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-[#2A2823]/60 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div
          data-testid="profile-menu"
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-2xl border border-[#B79256]/30 bg-[#FBF8F1]/95 backdrop-blur-xl shadow-[0_10px_40px_-12px_rgba(42,40,35,0.25)] overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-[#B79256]/20">
            <div
              data-testid="profile-menu-name"
              className="font-serif text-lg leading-tight text-[#2A2823] truncate"
            >
              {user.name}
            </div>
            <div className="text-xs text-[#2A2823]/60 truncate">{user.email}</div>
          </div>
          <Link
            to="/your-grove"
            role="menuitem"
            data-testid="menu-your-grove"
            onClick={() => setOpen(false)}
            className={itemCls}
          >
            <Bookmark className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75} />
            <span>Your Grove</span>
          </Link>
          <Link
            to={`/roots/${user.user_id}`}
            role="menuitem"
            data-testid="menu-roots"
            onClick={() => setOpen(false)}
            className={itemCls}
          >
            <User className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75} />
            <span>Roots (my profile)</span>
          </Link>
          <Link
            to="/gift-lists"
            role="menuitem"
            data-testid="menu-gift-lists"
            onClick={() => setOpen(false)}
            className={itemCls}
          >
            <Gift className="w-4 h-4 text-[#B79256]" strokeWidth={1.75} />
            <span>Gift Lists</span>
          </Link>
          <Link
            to="/planner/home"
            role="menuitem"
            data-testid="menu-planner"
            onClick={() => setOpen(false)}
            className={itemCls}
          >
            <CalendarCheck className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75} />
            <span>Willoah Planner</span>
          </Link>
          <button
            type="button"
            role="menuitem"
            data-testid="menu-signout"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className={`${itemCls} border-t border-[#B79256]/20`}
          >
            <LogOut className="w-4 h-4 text-[#2A2823]/70" strokeWidth={1.75} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
